import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ColorSchemeSelector, type ColorSchemeItem } from '@/components/shared/ColorSchemeSelector';
import { FontSetSelector } from '@/components/shared/FontSetSelector';
import { useToast } from '@stackje/shared-components/hooks/useToast';
import { captureErrorAndToast } from '@/utils/error-utils';
import { useAuth } from '@/hooks/useAuth';
import { useSiteQuery, useSiteUpdateMutation } from '@/hooks/useSiteQuery';
import { type ColorSchemeId } from '@stackje/common/colorScheme';
import { type FontSetId } from '@stackje/common/fontSet';
import { getAvailableColorSchemes } from '@/utils/color-scheme-utils';
import { Button } from "@stackje/shared-components/ui/button";
import { Label } from "@stackje/shared-components/ui/label";
import { getTemplateById, createConfigFromTemplate, type SiteTemplateId } from '@/utils/site-templates';
import { z } from 'zod';
import { ColorScheme } from '@stackje/common/colorScheme';
import { generateNewColors } from '@/utils/color-scheme-utils';
import { publishSite } from '@/utils/publish-utils';
import { ClearableInput } from '@stackje/shared-components/ui/clearable-fields';

const searchSchema = z.object({
  template: z.enum(['links', 'bio-page', 'business-page', 'portfolio', 'product-page', 'empty-canvas']).optional()
});

export const Route = createFileRoute('/_protected/setup-site-theme')({
  validateSearch: searchSchema,
  component: SetupSiteThemePage,
});


function SetupSiteThemePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_protected/setup-site-theme' });
  const { toast } = useToast();
  const { user } = useAuth(); // Use Better Auth context
  const [selectedColorScheme, setSelectedColorScheme] = useState<ColorSchemeId | null>('natural');
  const [selectedFontSet, setSelectedFontSet] = useState<FontSetId | null>('modern');
  const [availableColorSchemes, setAvailableColorSchemes] = useState<ColorSchemeItem[]>([]);
  const [siteTitle, setSiteTitle] = useState<string>('');

  const userId = user?.id;
  const userEmail = user?.email;
  const selectedTemplate = search.template ? getTemplateById(search.template as SiteTemplateId) : null;

  const { data: site, isLoading } = useSiteQuery(userId || undefined);
  const updateSiteMutation = useSiteUpdateMutation();

  // Update available color schemes when user email changes
  useEffect(() => {
    const schemes = getAvailableColorSchemes(userEmail || undefined);
    setAvailableColorSchemes(schemes);
  }, [userEmail]);

  // Initialize site title when site data loads
  useEffect(() => {
    if (site?.siteDetails?.title) {
      setSiteTitle(site.siteDetails.title);
    } else if (site?.subdomain) {
      // Use subdomain as default title if no title exists
      setSiteTitle(site.subdomain);
    }
  }, [site]);

  const handleSubmit = async () => {
    if (!selectedColorScheme || !selectedFontSet || !site || !userId || !siteTitle.trim()) {
      toast({
        description: 'Please fill in the site title and select both a color scheme and a font set.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Create config from template if available, otherwise use empty template
      const templateConfig = selectedTemplate
        ? createConfigFromTemplate(selectedTemplate, siteTitle.trim(), site.siteDetails?.description)
        : {
            title: siteTitle.trim(),
            description: site.siteDetails?.description,
            fontSetId: selectedFontSet,
            pages: [{ path: "/", title: siteTitle.trim(), cards: [] }],
          };

      // Apply selected color scheme to template cards
      const selectedSchemeColors = selectedColorScheme ? ColorScheme[selectedColorScheme] : ColorScheme.natural;
      const updatedPages = templateConfig.pages.map(page => ({
        ...page,
        cards: page.cards.map(card => {
          // Generate new colors for this card using the selected color scheme
          const newColors = generateNewColors(card.colors, selectedSchemeColors);
          return {
            ...card,
            colors: newColors
          };
        })
      }));

      const updatedSiteData = {
        siteDetails: {
          ...site.siteDetails,
          title: siteTitle.trim(),
          colorSchemeId: selectedColorScheme,
          fontSetId: selectedFontSet,
        },
        config: {
          ...templateConfig,
          title: siteTitle.trim(),
          fontSetId: selectedFontSet,
          pages: updatedPages,
        },
      };

      await updateSiteMutation.mutateAsync({
        siteId: site.id,
        userId: userId,
        data: updatedSiteData,
      });

      // Publish the site after applying the template and theme
      try {
        await publishSite(site.id);
        toast({
          description: 'Your site has been created and published successfully!',
        });
      } catch (publishError) {
        console.error('Failed to publish site after setup:', publishError);
        toast({
          description: 'Your site has been created successfully, but publishing failed. Your changes are saved.',
        });
      }

      navigate({ to: '/', replace: true });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Please try again.';
      captureErrorAndToast(error, toast, {
        description: errorMessage
      });
    }
  };

  const handleColorSchemeSelect = (id: string) => {
    setSelectedColorScheme(id as ColorSchemeId);
  };

  const handleFontSetSelect = (id: FontSetId) => {
    setSelectedFontSet(id);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col justify-between p-6 pb-safe pt-safe pl-safe pr-safe max-w-md mx-auto w-full">
      <h1 className="text-3xl font-bold mb-2 text-center text-gray-800">
        Finish setting up your site
      </h1>
      <p className="text-gray-600 mb-8 text-center">
        Give your site a title and customize its appearance. You can change these later.
      </p>

      <div className="space-y-6">
        <div>
          <Label htmlFor="siteTitle" className="text-xl font-semibold mb-3 text-gray-700">
            Site Title
          </Label>
          <ClearableInput
            id="siteTitle"
            value={siteTitle}
            onChange={(val) => setSiteTitle(val)}
            placeholder="My Awesome Site"
            className="mt-2"
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            This will be displayed in the browser tab and in the search results.
          </p>
        </div>
        {selectedTemplate && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{selectedTemplate.icon}</span>
              <div>
                <h3 className="font-semibold text-blue-900">Template: {selectedTemplate.name}</h3>
                <p className="text-sm text-blue-700">{selectedTemplate.description}</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-700">Color Scheme</h2>
          <ColorSchemeSelector
            schemes={availableColorSchemes}
            title={undefined}
            selectedSchemeId={selectedColorScheme}
            onSchemeSelect={handleColorSchemeSelect}
          />
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-3 text-gray-700">Font Set</h2>
          <FontSetSelector
            title={undefined}
            selectedFontSetId={selectedFontSet}
            onFontSetSelect={handleFontSetSelect}
            hideProFonts={true}
          />
        </div>
      </div>

      <div className="mt-10">
        <Button
          type="submit"
          className="w-full text-lg py-6 rounded-md"
          onClick={handleSubmit}
          disabled={updateSiteMutation.isPending || isLoading}
        >
          {updateSiteMutation.isPending ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
